import moment from 'moment';
import { Column } from "material-table";
import { useTranslation } from "react-i18next";
import { IBookingType } from 'common/src/types';
import { useSelector, useDispatch } from "react-redux";
import { useAlert } from "components/alert/AlertContext";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CustomMaterialTable } from 'components/CustomMaterialTable';
import { adminManualCompleteBooking } from 'common/src/actions/bookinglistactions';

const BookingHistory = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const bookinglistdata = useSelector((state) => state.bookinglistdata);

  if (!settings) return null;

  const handleManualComplete = async (booking: IBookingType) => {
    const notCanComplete = ["CANCELLED", "COMPLETE", "NEW"].includes(booking.status);

    if (notCanComplete) {
      showAlert({
        type: "WARNING",
        title: t('ALERT'),
        message: t("CANNOT_COMPLETE_BOOKING_STATUS", { status: t(booking.status) })
      });
      return;
    }

    try {
      await dispatch(adminManualCompleteBooking(booking));

      showAlert({
        type: "SUCCESS",
        title: t('success'),
        message: t("BOOKING_COMPLETED_SUCCESSFULLY")
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      showAlert({
        type: "ERROR",
        title: t('error'),
        message: errorMessage || t("ERROR_COMPLETING_BOOKING")
      });
    }
  };

  const columns: Column<IBookingType>[] = [
    {
      title: t('booking_status'),
      field: 'status',
      render: (rowData: IBookingType) => (
        <span style={{
          color: rowData.status === "COMPLETE" ? "green" :
            rowData.status === "CANCELLED" ? "red" :
              rowData.status === "STARTED" ? "blue" : "orange"
        }}>
          {t(rowData.status)}
        </span>
      )
    },
    { title: t('booking_id'), field: 'id' },
    { title: t('booking_date'), field: 'bookingDate', render: (rowData: IBookingType) => rowData.bookingDate ? moment(rowData.bookingDate).format('DD/MM/YYYY HH:mm') : null },
    { title: t('trip_start_time'), field: 'tripdate', render: (rowData: IBookingType) => rowData.tripdate ? moment(rowData.tripdate).format('DD/MM/YYYY HH:mm') : null },
    { title: t('car_type'), field: 'carType' },
    { title: t('customer_name'), field: 'customer_name' },
    { title: t('pickup_address'), field: 'pickupAddress' },
    { title: t('drop_address'), field: 'dropAddress' },
    { title: t('assign_driver'), field: 'driver_name' },
    { title: t('cancellation_reason'), field: 'reason' },
    { title: t('cancellationFee'), render: (rowData: IBookingType) => <span>{rowData.cancellationFee ? rowData.cancellationFee : (0).toFixed(settings.decimal)}</span> },
    { title: t('otp'), field: 'otp' },
    { title: t('trip_cost'), field: 'trip_cost' },
    { title: t('trip_start_time'), field: 'trip_start_time' },
    { title: t('trip_end_time'), field: 'trip_end_time' },
    { title: t('total_time'), field: 'total_trip_time' },
    { title: t('distance'), field: 'distance' },
    { title: t('vehicle_no'), field: 'vehicle_number' },
    { title: t('trip_cost_driver_share'), field: 'driver_share' },
    { title: t('convenience_fee'), field: 'convenience_fees' },
    { title: t('discount_ammount'), field: 'discount' },
    { title: t('Customer_paid'), field: 'customer_paid' },
    { title: t('booking_ref'), field: 'reference' },
    {
      field: 'payment_mode',
      title: t('payment_mode'),
      render: (rowData: IBookingType) => <span>{t(rowData.real_payment_mode as any)}</span>,
    },
  ];

  return (
    <CustomMaterialTable
      columns={columns}
      title={'booking_title'}
      data={bookinglistdata.bookings ?? []}
      actions={[
        (rowData: IBookingType) => ({
          icon: CheckCircleIcon,
          tooltip: t("complete_booking_manually"),
          onClick: () => handleManualComplete(rowData),
        })
      ]}
    />
  );
}

export default BookingHistory;